import React, { useState, useRef, useEffect } from "react";
import emailjs from "@emailjs/browser";
import "./Contact.css";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    privacy: false,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const honeypotRef = useRef(null);

  // EmailJS configuration - Replace these with your actual values
  // Get these from https://www.emailjs.com/
  const EMAILJS_SERVICE_ID =
    process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_puux4hi";
  const EMAILJS_TEMPLATE_ID =
    process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_63s42ra";
  const EMAILJS_PUBLIC_KEY =
    process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "5VHjKWrFUUto8ta2d";

  // Initialize EmailJS
  useEffect(() => {
    if (EMAILJS_PUBLIC_KEY && EMAILJS_PUBLIC_KEY !== "5VHjKWrFUUto8ta2d") {
      emailjs.init(EMAILJS_PUBLIC_KEY);
    }
  }, []);

  // Animation delays for contact info items
  const contactItems = [
    {
      icon: "📧",
      title: "Email",
      info: "team.assecure.in@gmail.com",
      link: "mailto:team.assecure.in@gmail.com",
      delay: 0.1,
    },
    {
      icon: "📱",
      title: "Phone",
      info: "+91 70582 27939",
      link: "tel:+917058227939",
      delay: 0.2,
    },
    {
      icon: "📍",
      title: "Location",
      info: "Pune, MH, IN",
      link: "#",
      delay: 0.3,
    },
  ];

  // Add animation delays to contact info items
  useEffect(() => {
    const items = document.querySelectorAll(".contact-info-item");
    items.forEach((item, index) => {
      item.style.animationDelay = `${contactItems[index].delay}s`;
    });
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    } else if (!/^[A-Za-z\s]+$/.test(formData.name.trim())) {
      newErrors.name = "Name can only contain letters and spaces";
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Phone validation (optional but must be valid if provided)
    if (formData.phone && !/^[0-9\s\-\+\(\)]+$/.test(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }

    // Subject validation
    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    } else if (formData.subject.trim().length < 3) {
      newErrors.subject = "Subject must be at least 3 characters";
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    } else if (formData.message.length > 2000) {
      newErrors.message = "Message must be less than 2000 characters";
    }

    // Privacy checkbox validation
    if (!formData.privacy) {
      newErrors.privacy = "You must agree to the Privacy Policy";
    }

    // Honeypot check (spam protection)
    if (honeypotRef.current && honeypotRef.current.value) {
      newErrors.honeypot = "Spam detected";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Check if EmailJS is configured
    if (
      EMAILJS_SERVICE_ID === "YOUR_SERVICE_ID" ||
      EMAILJS_TEMPLATE_ID === "YOUR_TEMPLATE_ID" ||
      EMAILJS_PUBLIC_KEY === "YOUR_PUBLIC_KEY"
    ) {
      setSubmitStatus({
        type: "error",
        message:
          "Email service is not configured. Please set up EmailJS credentials.",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Prepare template parameters for EmailJS
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone || "Not provided",
        subject: formData.subject,
        message: formData.message,
        to_email: "team.assecure.in@gmail.com", // Your receiving email
      };

      // Send email using EmailJS
      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      if (response.status === 200) {
        setSubmitStatus({
          type: "success",
          message: "Message sent successfully! We'll get back to you soon.",
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
          privacy: false,
        });
        if (honeypotRef.current) {
          honeypotRef.current.value = "";
        }
      } else {
        throw new Error("Failed to send message");
      }
    } catch (error) {
      console.error("EmailJS Error:", error);
      setSubmitStatus({
        type: "error",
        message: `
    Failed to send message. Please try again later or contact us directly at 
    <a href="mailto:team.assecure.in@gmail.com">team.assecure.in@gmail.com</a> 
    or 
    <a href="tel:+917058227939">+91 70582 27939</a>.
  `,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact" id="contact">
      <div className="contact-container">
        <div className="contact-content">
          {/* Contact Info Section */}
          <div className="contact-info">
            <h2 className="contact-info-title">Get In Touch</h2>
            <p className="contact-info-description">
              Have a project in mind? Want to collaborate? Or just want to say
              hello? We'd love to hear from you. Send us a message and we'll
              respond as soon as possible.
            </p>

            <div className="contact-info-list">
              {contactItems.map((item, index) => (
                <a
                  key={index}
                  href={item.link}
                  className="contact-info-item"
                  style={{ animationDelay: `${item.delay}s` }}
                >
                  <div className="contact-info-icon">{item.icon}</div>
                  <div className="contact-info-text">
                    <h4>{item.title}</h4>
                    <p>{item.info}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Contact Form Section */}
          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                minLength="2"
                maxLength="100"
                pattern="[A-Za-z\s]+"
                autoComplete="name"
                placeholder="Enter your full name"
                className={errors.name ? "error" : ""}
              />
              {errors.name && (
                <span className="error-message">{errors.name}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                maxLength="255"
                autoComplete="email"
                placeholder="your.email@example.com"
                className={errors.email ? "error" : ""}
              />
              {errors.email && (
                <span className="error-message">{errors.email}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                maxLength="20"
                pattern="[0-9\s\-\+\(\)]+"
                autoComplete="tel"
                placeholder="+1 (555) 123-4567"
                className={errors.phone ? "error" : ""}
              />
              {errors.phone && (
                <span className="error-message">{errors.phone}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="subject">Subject *</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                minLength="3"
                maxLength="200"
                placeholder="What is this regarding?"
                className={errors.subject ? "error" : ""}
              />
              {errors.subject && (
                <span className="error-message">{errors.subject}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                minLength="10"
                maxLength="2000"
                rows="6"
                placeholder="Tell us about your project or inquiry..."
                className={errors.message ? "error" : ""}
              ></textarea>
              <div className="char-count">
                <span>{formData.message.length}</span>/2000
              </div>
              {errors.message && (
                <span className="error-message">{errors.message}</span>
              )}
            </div>

            {/* Honeypot field for spam protection */}
            <div style={{ display: "none" }}>
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                ref={honeypotRef}
                tabIndex="-1"
                autoComplete="off"
              />
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  id="privacy"
                  name="privacy"
                  checked={formData.privacy}
                  onChange={handleChange}
                  required
                />
                <span>
                  I agree to the{" "}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>{" "}
                  *
                </span>
              </label>
              {errors.privacy && (
                <span className="error-message">{errors.privacy}</span>
              )}
            </div>

            {submitStatus && (
              <div
                className={`form-message ${submitStatus.type}`}
                dangerouslySetInnerHTML={{ __html: submitStatus.message }}
              ></div>
            )}

            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="btn-loader">Sending...</span>
                </>
              ) : (
                <>
                  <span className="btn-text">Send Message</span>
                  <span className="btn-arrow">→</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
